<template>
    <!-- The organisation's letterhead on printed documents (Settings > بيانات المؤسسة).
         id and class given by the page land on this element: the documents use id="header" to
         print with or without the letterhead (they toggle .no-print on it). Nothing is drawn,
         not even the rule under it, while the organisation has no name and no logo. -->
    <header v-show="!isEmpty" class="print-letterhead" :class="compact ? 'print-letterhead--compact' : 'print-letterhead--full'">
        <!-- Report prints: one line, the logo, then the parent body and the organisation -->
        <div v-if="compact && !isEmpty" class="print-letterhead__line">
            <img v-if="logoUrl" :src="logoUrl" :alt="org.nameAr" class="print-letterhead__logo"
                :class="{ 'print-letterhead__logo--wide': wideLogo }" @load="measureLogo">
            <span v-if="lineAr" class="print-letterhead__ar">{{ lineAr }}</span>
            <span v-if="lineEn" class="print-letterhead__en" dir="ltr">{{ lineEn }}</span>
        </div>

        <!-- Documents: Arabic on the right, the logo in the centre, English on the left -->
        <template v-else-if="!compact">
            <div class="title-right">
                <h2 v-if="org.parentNameAr">{{ org.parentNameAr }}</h2>
                <h3 v-if="org.nameAr">{{ org.nameAr }}</h3>
            </div>
            <img v-if="logoUrl" :src="logoUrl" :alt="org.nameAr" class="image-center"
                :class="{ 'image-center--wide': wideLogo }" @load="measureLogo">
            <div class="title-left" dir="ltr">
                <h2 v-if="org.parentNameEn">{{ org.parentNameEn }}</h2>
                <h3 v-if="org.nameEn">{{ org.nameEn }}</h3>
            </div>
        </template>
    </header>
</template>

<script>
// "وزارة الصحة - المركز ..." without a stray dash when one of the two is missing.
function joinNames(parent, name) {
    return [parent, name].filter(Boolean).join(" - ");
}

export default {
    props: {
        // A smaller one-line letterhead for report prints.
        compact: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            // A wordmark (much wider than high) gets a wider box, or it would print a few pixels high.
            wideLogo: false,
        };
    },
    methods: {
        measureLogo(event) {
            const { naturalWidth, naturalHeight } = event.target;
            this.wideLogo = naturalHeight > 0 && naturalWidth / naturalHeight >= 2;
        },
    },
    computed: {
        org() {
            return this.$store.state.organization.org;
        },
        logoUrl() {
            return this.$store.getters["organization/logoUrl"];
        },
        lineAr() {
            return joinNames(this.org.parentNameAr, this.org.nameAr);
        },
        lineEn() {
            return joinNames(this.org.parentNameEn, this.org.nameEn);
        },
        isEmpty() {
            return !this.logoUrl && !this.lineAr && !this.lineEn;
        },
    },
};
</script>

<style scoped>
/* Documents: three equal columns, so the logo stays in the middle of the page */
.print-letterhead--full {
    display: flex;
    align-items: center;
    padding: 10px 0px;
    width: 100%;
    border-bottom: 1px solid;
    background-color: #fff;
}

.title-right,
.title-left {
    flex: 1 1 0;
    text-align: center;
}

.title-right {
    padding-right: 2px;
}

.title-left {
    padding-left: 2px;
}

.title-right h2,
.title-left h2 {
    font-size: 24px;
}

.image-center {
    max-height: 100px;
    /* يمكنك تعديل الحجم حسب الحاجة */
    max-width: 120px;
    /* يمكنك تعديل الحجم حسب الحاجة */
    margin: 0 10px;
    /* مسافة بين الصورة والعناوين */
}

.image-center--wide {
    max-height: 80px;
    max-width: 300px;
}

/* Report prints: the root stays a block (.print-only makes it one on paper), the line is the row */
.print-letterhead__line {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 4px;
    margin-bottom: 6px;
    border-bottom: 1px solid;
}

.print-letterhead__logo {
    max-height: 36px;
    max-width: 120px;
}

.print-letterhead__logo--wide {
    max-height: 40px;
    max-width: 180px;
}

.print-letterhead__ar {
    font-size: 14px;
    font-weight: bold;
}

/* The pages are right-to-left: this pushes the English name to the left end of the line. */
.print-letterhead__en {
    margin-right: auto;
    font-size: 12px;
}
</style>
